import React, { useMemo } from 'react';
import { LearningNavItem } from '../../Learning/types';
import { MoodleInstanceKey } from '../../../../support/slices/moodleSlice';
import { useMoodlePagesByCourses } from '../../../../hooks/useMoodleHooks';
import MoodlePageRenderer from '../../Learning/providers/moodle/renderers/MoodlePageRenderer';
import MoodleLessonRenderer from '../../Learning/providers/moodle/renderers/MoodleLessonRenderer';
import MoodleQuizRenderer from '../../Learning/providers/moodle/renderers/MoodleQuizRenderer';
import MoodleForum from '../Forum/MoodleForum';
import MoodleFeedbackResource from '../Resource/MoodleFeedbackResource';


interface Props {
  courseId: string;
  item: LearningNavItem;
  instance: MoodleInstanceKey;
}

function FallbackCard({
                        title,
                        description,
                        link,
                      }: {
  title: string;
  description: string;
  link?: string;
}) {
  return (
    <div style={{ padding: '1.5rem' }}>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <p>{description}</p>
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          style={{ color: '#2563eb', textDecoration: 'underline' }}
        >
          Open in Moodle
        </a>
      )}
    </div>
  );
}

function UnsupportedContent({ module }: { module: any }) {
  return (
    <div style={{ padding: '1.5rem' }}>
      <h2 style={{ marginTop: 0 }}>{module?.name ?? 'Unsupported content'}</h2>
      <p>This content type is not yet supported in the unified renderer.</p>
      <pre
        style={{
          background: '#f8fafc',
          padding: '1rem',
          borderRadius: 8,
          overflow: 'auto',
          fontSize: '0.8rem',
        }}
      >
        {JSON.stringify(module, null, 2)}
      </pre>
    </div>
  );
}

export default function MoodleLearningRenderer({ courseId, item, instance }: Props) {
  const courseIdNum = Number(courseId);
  const module = item.raw;
  const resolvedType = String(item.type || module?.modname || '').toLowerCase();
  const resolvedModname = String(module?.modname || '').toLowerCase();

  const { data: pages = [] } = useMoodlePagesByCourses(
    courseIdNum,
    { enabled: resolvedType === 'page' && !!courseIdNum, retry: false },
    instance
  );

  const pagesById = useMemo<Record<number, any>>(() => {
    const map: Record<number, any> = {};

    for (const p of pages as any[]) {
      if (!p) continue;
      if (typeof p.id === 'number') map[p.id] = p;
      if (typeof p.coursemodule === 'number') map[p.coursemodule] = p;
    }

    return map;
  }, [pages]);

  if (!module) {
    return <div style={{ padding: '2rem' }}>No content found.</div>;
  }

  if (resolvedType === 'page') {
    const pageObj =
      pagesById[Number(module.id)] ??
      (typeof module.instance === 'number' ? pagesById[Number(module.instance)] : undefined);

    return (
      <MoodlePageRenderer
        pageObj={pageObj}
        fallbackTitle={module.name}
        instance={instance}
      />
    );
  }

  if (resolvedType === 'lesson') {
    return (
      <MoodleLessonRenderer
        courseId={courseId}
        lessonId={Number(module.instance)}
        instance={instance}
      />
    );
  }

  if (resolvedType === 'quiz') {
    return (
      <MoodleQuizRenderer
        courseId={courseId}
        quizId={Number(module.instance)}
        instance={instance}
      />
    );
  }

  if (resolvedType === 'forum') {
    return (
      <MoodleForum
        courseId={Number(courseId)}
        forumId={Number(module.instance)}
        instance={instance}
      />
    );
  }

  if (resolvedType === 'feedback' || resolvedModname === 'feedback') {
    return (
      <div style={{ padding: '1.5rem' }}>
        <h2 style={{ marginTop: 0 }}>{module.name || 'Feedback'}</h2>
        <MoodleFeedbackResource module={module} instance={instance} />
      </div>
    );
  }

  if (resolvedType === 'resource') {
    return (
      <FallbackCard
        title={module.name || 'Resource'}
        description="This resource is not yet fully embedded in the unified renderer."
        link={module.url}
      />
    );
  }

  if (resolvedType === 'url') {
    return (
      <FallbackCard
        title={module.name || 'External URL'}
        description="This URL activity opens in Moodle."
        link={module.url}
      />
    );
  }

  if (resolvedType === 'folder') {
    return (
      <FallbackCard
        title={module.name || 'Folder'}
        description="Folder content is not yet fully embedded in the unified renderer."
        link={module.url}
      />
    );
  }

  return <UnsupportedContent module={module} />;
}